import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Check } from "lucide-react";
import { useConsent } from "./ConsentContext";
import { useLang } from "../i18n";
import { COOKIE_CATEGORIES } from "../config/siteConfig";

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} disabled={disabled}
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${
        checked ? "wanm-gradient-bg" : "bg-wanm-dark/20"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

export function PreferenceCenter() {
  const { prefOpen, closePreferences, savePreferences, consent } = useConsent();
  const { t, lang } = useLang();
  const [local, setLocal] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (prefOpen && consent) setLocal({ analytics: !!consent.analytics, marketing: !!consent.marketing });
  }, [prefOpen, consent]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closePreferences(); };
    if (prefOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prefOpen, closePreferences]);

  return (
    <AnimatePresence>
      {prefOpen && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="cookie-preference-center"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closePreferences} />
          <motion.div
            role="dialog" aria-modal="true" aria-label={t.cookies.prefTitle}
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
          >
            <div className="h-1.5 w-full wanm-gradient-bg sticky top-0" />
            <div className="p-7 md:p-9">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-2xl font-black text-wanm-dark">{t.cookies.prefTitle}</h2>
                <button onClick={closePreferences} aria-label={t.cookies.cancel} data-testid="pref-close"
                  className="text-wanm-dark/50 hover:text-wanm-red transition-colors">
                  <X size={24} />
                </button>
              </div>
              <p className="mt-3 text-sm text-wanm-dark/60 leading-relaxed">{t.cookies.prefIntro}</p>

              <div className="mt-6 space-y-4">
                {COOKIE_CATEGORIES.map((cat) => {
                  const copy = cat[lang] || cat.en;
                  const checked = cat.required ? true : local[cat.id];
                  return (
                    <div key={cat.id} className="rounded-2xl border border-wanm-offwhite p-5">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="font-black text-wanm-dark">{copy.name}</h3>
                        {cat.required ? (
                          <span className="text-xs font-bold uppercase tracking-widest wanm-gradient-text">{t.cookies.always}</span>
                        ) : (
                          <Toggle checked={checked} onChange={(v) => setLocal((p) => ({ ...p, [cat.id]: v }))} testId={`toggle-${cat.id}`} />
                        )}
                      </div>
                      <p className="mt-2 text-sm text-wanm-dark/60 leading-relaxed">{copy.desc}</p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button onClick={() => savePreferences(local)} data-testid="pref-save"
                  className="flex-1 inline-flex items-center justify-center gap-2 wanm-gradient-bg text-white font-bold rounded-full py-3 hover:scale-[1.02] transition-transform">
                  <Check size={18} /> {t.cookies.save}
                </button>
              </div>
              <div className="mt-5 flex items-center gap-4 text-sm">
                <Link to="/cookie-policy" onClick={closePreferences} className="font-bold text-wanm-red underline underline-offset-2">{t.cookies.policyLink}</Link>
                <Link to="/privacy" onClick={closePreferences} className="font-bold text-wanm-dark/60 hover:text-wanm-dark">{t.cookies.privacyLink}</Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PreferenceCenter;
